// src/components/OnboardingContainer.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { prevStep, setStep } from "../../../stepSlice";
import Step4Page from "../Sections/Step4Page";
import Step5Page from "../Sections/Step5Page";
import Step6Page from "../Sections/Step6Page";
import Step7Page from "../Sections/Step7Page";
import Step8Page from "../Sections/Step8Page";

import "../../CSS/NewOnboading/NewOnboading.css";
import "../../CSS/NewOnboading/Step6.css";
import ProgressBar from "../Pages/ProgressBar";
import {
  get_all_data_onboarding,
  server_post_data,
} from "../../../ServiceConnection/serviceconnection";
import { handleError } from "../../../CommonJquery/CommonJquery";

// Step numbers backend ke flags se match karte hain (4..8).
// Steps 2 (plan) aur 3 (category) is container ke bahar hain.
const FIRST_STEP = 4;
const LAST_STEP = 8;
const TOTAL_STEPS = LAST_STEP - FIRST_STEP + 1;

const clampStep = (n) => {
  const num = parseInt(n);
  if (isNaN(num)) return FIRST_STEP;
  if (num < FIRST_STEP) return FIRST_STEP;
  if (num > LAST_STEP) return LAST_STEP;
  return num;
};

export default function OnboardingContainer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentStep_old =
    useSelector((state) => state.step.currentStep) || FIRST_STEP;

  const [currentStep, setcurrentStep] = useState(clampStep(currentStep_old));
  const [direction, setDirection] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [visibleStep, setVisibleStep] = useState(-1);
  const [maxStepAllowed, setMaxStepAllowed] = useState(FIRST_STEP);
  const [ShowLoaderAdmin, setShowLoaderAdmin] = useState(false);

  const stepContainerRef = useRef(null);
  // useRef, module-level variable nahi — warna remount pe step restore
  // dobara nahi chalta aur user galat step pe atak jaata hai
  const dataLoadedRef = useRef(false);

  useEffect(() => {
    if (dataLoadedRef.current && !animating) {
      setVisibleStep(currentStep);
    }
  }, [currentStep, animating]);

  const handleTransition = (newDirection, stepAction) => {
    if (animating) return;

    setDirection(newDirection);
    setAnimating(true);
    const exitClass =
      newDirection === "next" ? "slide-out-left" : "slide-out-right";
    if (stepContainerRef.current) {
      stepContainerRef.current.className = `step-container ${exitClass}`;
    }
    setTimeout(() => {
      stepAction();
      setTimeout(() => {
        setAnimating(false);
      }, 50);
    }, 300);
  };

  const applyStep = useCallback(
    (step) => {
      const target = clampStep(step);
      setVisibleStep(target);
      setcurrentStep(target);
      dispatch(setStep(target)); // dispatch ke bina redux update nahi hota
    },
    [dispatch],
  );

  const master_data_get = useCallback(async () => {
    setShowLoaderAdmin(true);
    let fd = new FormData();
    fd.append("flag", "6");
    await server_post_data(get_all_data_onboarding, fd)
      .then((Response) => {
        let data = Response.message.split("~@~");
        const final_data = JSON.parse(data[1]);

        if (final_data.error) {
          handleError(final_data.message);
          setShowLoaderAdmin(false);
          return;
        }

        if (dataLoadedRef.current) {
          setShowLoaderAdmin(false);
          return;
        }
        dataLoadedRef.current = true;

        // step_save server se — top-level ya onboarding_data me
        const onboarding = final_data.onboarding_data || [];
        const savedStep = parseInt(
          final_data.step_save ?? onboarding[0]?.step_save ?? 0,
        );

        // 9 = onboarding complete
        if (savedStep >= 9) {
          navigate("/qr_ready_success");
          return;
        }

        const target = clampStep(savedStep);
        applyStep(target);
        setMaxStepAllowed(target);

        setShowLoaderAdmin(false);
      })
      .catch(() => {
        handleError("network");
        setShowLoaderAdmin(false);
      });
  }, [applyStep, navigate]);
  useEffect(() => {
    master_data_get();
  }, [master_data_get]);

  const handleNext = (serverStepSave) => {
    const fromServer = parseInt(serverStepSave);
    const nextStepNumber = clampStep(
      isNaN(fromServer) ? currentStep + 1 : fromServer,
    );

    if (nextStepNumber <= currentStep && currentStep >= LAST_STEP) return;

    if (nextStepNumber > maxStepAllowed) {
      setMaxStepAllowed(nextStepNumber);
    }

    setcurrentStep(nextStepNumber);
    handleTransition("next", () => dispatch(setStep(nextStepNumber)));
  };

  const handleBack = () => {
    if (currentStep <= FIRST_STEP) return;

    const prevStepNumber = currentStep - 1;
    setcurrentStep(prevStepNumber);
    handleTransition("prev", () => dispatch(setStep(prevStepNumber)));
  };
  const getAnimationClass = () => {
    if (direction === null) return "";
    return direction === "next" ? "slide-in-right" : "slide-in-left";
  };

  const goToStep = (stepNumber) => {
    if (animating) return;

    // Plan step (2) is container ke bahar hai — upgrade link usko bhejta hai
    if (parseInt(stepNumber) < FIRST_STEP) {
      navigate("/price_plan");
      return;
    }

    const target = clampStep(stepNumber);

    if (target > maxStepAllowed) {
      console.warn("Step not allowed yet!");
      return;
    }

    handleTransition(target > currentStep ? "next" : "prev", () => {
      setcurrentStep(target);
      setVisibleStep(target);
      dispatch(setStep(target));
    });
  };

  const renderStep = (stepNumber) => {
    switch (stepNumber) {
      case 4:
        return (
          <Step4Page
            onBack={handleBack}
            onNext={handleNext}
            goToStep={goToStep}
          />
        );
      case 5:
        return (
          <Step5Page
            onBack={handleBack}
            onNext={handleNext}
            goToStep={goToStep}
          />
        );
      case 6:
        return (
          <Step6Page
            onBack={handleBack}
            onNext={handleNext}
            goToStep={goToStep}
          />
        );
      case 7:
        return (
          <Step7Page
            onBack={handleBack}
            onNext={handleNext}
            goToStep={goToStep}
          />
        );
      case 8:
        return (
          <Step8Page
            onBack={handleBack}
            onNext={handleNext}
            goToStep={goToStep}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div
        className={
          currentStep === 7
            ? "step7containerClss"
            : currentStep === 4 || currentStep === 6
              ? "step0and1"
              : "colorBg"
        }
      >
        <ProgressBar
          currentStep={currentStep}
          firstStep={FIRST_STEP}
          totalSteps={TOTAL_STEPS}
          goToStep={goToStep}
          maxStepAllowed={maxStepAllowed}
        />

        <div
          className={
            currentStep === 4
              ? "onboarding_cointrr"
              : currentStep === 6
                ? "container-xl"
                : "container-lg"
          }
          style={{
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            ref={stepContainerRef}
            className={`step-container ${animating ? "" : getAnimationClass()}`}
          >
            {renderStep(visibleStep)}
          </div>
        </div>
      </div>
    </>
  );
}
